import React, { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAccountSubscriptionsQuery } from "fond/api";
import { DesignSubscription, Maybe, MultiProject, Project, Subscription, SubscriptionStatus } from "fond/types";
import { isExtendedPlannerProject } from "fond/utils/project";

export type QuotaUsageDetails = {
  // The total quota available
  AvailableQuota: number;
  // The amount of datapack quota that has ever been bought.
  // Note this is not renewed / reset on a billing cycle.
  DataPackQuota: number;
  // The amount of datapack quota that has ever been used
  // Note this is not renewed / reset on a billing cycle.
  DataPackUsage: number;
  // Base Planner add-on quota available for the billing cycle
  Quota: number;
  // The amount of base Planner add-on quota already used in the billing cycle
  QuotaUsage: number;
};

type ActiveSubscriptionQuotas = (DesignSubscription | DesignSubscription) & Pick<Subscription, "DataPackQuota" | "DataPackUsage">;

/**
 * Hook to return the relevant quota and quota usage for a given project.
 */
export const useProjectQuota = (project: Project | MultiProject | null): { quota: Maybe<QuotaUsageDetails>; isLoaded: boolean } => {
  const { data: subscriptions, isSuccess: isLoaded } = useGetAccountSubscriptionsQuery(project?.Account?.ID ?? skipToken);

  // "FOND design" (planner_extended) projects consume design quota, while other projects consume 'planner' quota.
  const activeSubscription: ActiveSubscriptionQuotas | null = useMemo(() => {
    const isExtendedProject = isExtendedPlannerProject(project);
    const subscription = subscriptions?.find((sub) => sub.Status === SubscriptionStatus.Active && (isExtendedProject ? !!sub.Design : !!sub.Planner));

    const subType = isExtendedProject ? subscription?.Design : subscription?.Planner;

    return subType
      ? {
          ...subType,
          DataPackQuota: (isExtendedProject ? subscription?.DesignDataPackQuota : subscription?.DataPackQuota) ?? 0,
          DataPackUsage: (isExtendedProject ? subscription?.DesignDataPackUsage : subscription?.DataPackUsage) ?? 0,
        }
      : null;
  }, [project, subscriptions]);

  const quota = useMemo(() => {
    if (!activeSubscription) {
      return null;
    }
    const { Quota, QuotaUsage, DataPackQuota, DataPackUsage } = activeSubscription;
    return {
      Quota,
      QuotaUsage,
      DataPackQuota,
      DataPackUsage,
      AvailableQuota: Quota + DataPackQuota - QuotaUsage - DataPackUsage,
    };
  }, [activeSubscription]);

  return useMemo(() => ({ quota, isLoaded }), [quota, isLoaded]);
};
